import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { AyenMapComponent } from 'ayen-google-map';
import { LocationsService } from 'src/app/core/services/locations/locations.service';
import { ScriptService } from 'src/app/core/services/script/script.service';
import { PropertyUnit } from 'src/app/models/interfaces/property-unit';
import { GlobalConstants } from 'src/app/shared/global/map';
import { City, District, Region } from 'src/app/shared/Models/locations.model';

@Component({
    selector: 'property-unit-location',
    templateUrl: './property-unit-location.component.html',
    styleUrls: ['./property-unit-location.component.scss'],
    standalone: false
})
export class PropertyUnitLocationComponent implements OnInit {

  @Input() unitDetails: PropertyUnit | null = null;
  @ViewChild('ayenMap') ayenMap!: AyenMapComponent;
  
  apiLoaded: boolean = false;
  regions: Region[] = [];
  cities: City[] = [];
  districts: District[] = [];
  latitude: string = GlobalConstants.latitude;
  longitude: string = GlobalConstants.longitude;
  nationalAddressCode: string = '';
  hasNationalCodeError: boolean = false;
  shortAddressPattern = /^[A-Z]{4}[0-9]{4}$/;

  @Input({ required: true }) propertyDetailsFormGroup!: FormGroup;

  constructor(
    private location: LocationsService,
    private GoogleMapLoader: ScriptService,
  ) { }

  ngOnInit(): void {
    this.GoogleMapLoader.obsCurrentApiStatus.subscribe((status: Boolean) => {
      this.apiLoaded = status.valueOf();
        if (this.unitDetails?.location) {
          const loc = this.unitDetails.location.split(',');
          this.latitude = loc[0];
          this.longitude = loc[1];
          this.ayenMap?.reCenterMap(+this.latitude, +this.longitude);
          this.patchLocationValues();
        }
    });
    this.getRegions();
  }

  isShortAddressInvalid(): boolean {
    const value = this.propertyDetailsFormGroup.get('short_address')?.value;
    return value && !this.shortAddressPattern.test(value);
  }

  patchLocationValues(): void {
    if (this.unitDetails) {
      this.propertyDetailsFormGroup.patchValue({
        location: this.unitDetails.location,
        address_area: this.unitDetails.address_area? this.unitDetails.address_area.id : null,
        address_city: this.unitDetails.address_city? this.unitDetails.address_city.id : null,
        district: this.unitDetails.district? this.unitDetails.district.id : null,
        address_street: this.unitDetails.address_street,
        short_address: this.unitDetails.short_address,
        address_building_code: this.unitDetails.address_building_code,
        landmark: this.unitDetails.landmark,
      });

      // Manually trigger cascading data loads after patching
      if (this.unitDetails.address_area) {
        this.getCitiesByRegion(Number(this.unitDetails.address_area.id));
      }
      if (this.unitDetails.address_city) {
        this.getDistrictsByCity(Number(this.unitDetails.address_city.id));
      }
    }
  }

  getRegions(): void {
    this.location.getAllRegions().subscribe((data: Region[]) => { this.regions = data; });
  }

  regionChanged(): void {
    this.propertyDetailsFormGroup.get('address_city')?.setValue(null);
    this.propertyDetailsFormGroup.get('district')?.setValue(null);
    this.districts = [];
    const regionIdValue = this.propertyDetailsFormGroup.get('address_area')?.value;
    const regionId = regionIdValue ? Number(regionIdValue) : null;
    if (regionId) {
      this.getCitiesByRegion(regionId);
    }
  }

  getCitiesByRegion(regionId: number): void {
    this.location.getCitiesByRegionPK(regionId).subscribe((data: City[]) => { this.cities = data; });
  }

  getDestrictsByCity(): void {
    const cityIdValue = this.propertyDetailsFormGroup.get('address_city')?.value;
    const cityId = cityIdValue ? Number(cityIdValue) : null;

    if (cityId) {
      this.propertyDetailsFormGroup.get('district')?.setValue(null);
      this.getDistrictsByCity(cityId);
    }
  }

  getDistrictsByCity(cityId: number): void {
    this.location.getDistrictsByCityPK(cityId).subscribe((data: District[]) => {
      this.districts = data;
      if (data.length === 0) {
        this.propertyDetailsFormGroup.get('district')?.clearValidators();
        this.propertyDetailsFormGroup.get('district')?.setValue(null);
        this.propertyDetailsFormGroup.get('district')?.updateValueAndValidity();
      } else {
        this.propertyDetailsFormGroup.get('district')?.setValidators([Validators.required]);
        this.propertyDetailsFormGroup.get('district')?.updateValueAndValidity();
      }
    });
  }

  mapValues(event: any): void {
    if (event.latitude && event.longitude) {
      this.latitude = event.latitude;
      this.longitude = event.longitude;
      const locationString = `${this.latitude},${this.longitude}`;
      this.propertyDetailsFormGroup.get('location')?.setValue(locationString);

      this.location.getNationalAddressByLatLong(event.latitude, event.longitude)
      .subscribe((data: any) => {
        const nationalAdressData = data.data;
        this.propertyDetailsFormGroup.patchValue({
          address_area: nationalAdressData?.address_area || null,
          address_city: nationalAdressData?.address_city || null,
          district: nationalAdressData?.district || null,
          address_street: nationalAdressData?.address_street || '',
          short_address: nationalAdressData?.short_address || '',
          address_building_code: nationalAdressData?.address_building_code || '',
        });

        if (nationalAdressData?.address_area) {
          this.getCitiesByRegion(Number(nationalAdressData.address_area));
        }
        if (nationalAdressData?.address_city) {
          this.getDistrictsByCity(Number(nationalAdressData.address_city));
        }
      });
      
    }
  }

  formatNationalCode(value: string): void {
    if (!value) {
      this.hasNationalCodeError = false;
      this.nationalAddressCode = '';
      return;
    }
    
    value = value.replace(/\s/g, '').replace(/[^a-zA-Z0-9]/g, '');

    const letters = value.substring(0, 4).toUpperCase();
    const numbers = value.substring(4, 8);

    if (value.length > 4) {
      this.nationalAddressCode = `${letters} ${numbers}`;
    } else {
      this.nationalAddressCode = letters;
    }

    const isValidPattern = /^[A-Za-z]{4}[0-9]{4}$/.test(value);
    this.hasNationalCodeError = value.length > 0 && !isValidPattern;
  }
}
